/**
 * Base MiniKit Integration (2025 Standard)
 * -----------------------------------------
 * Base App official MiniKit integration using OnchainKit.
 * 
 * CRITICAL CLARIFICATION (Dec 2025):
 * - Base DOES have an official MiniKit (via OnchainKit)
 * - It uses `window.miniKit` API (same as Farcaster, but different context)
 * - Detection requires checking miniKit.isFrameContext === false (Base App)
 *   vs miniKit.isFrameContext === true (Farcaster Frame)
 * 
 * @see https://docs.base.org/base-app/build-with-minikit/overview
 * @see https://onchainkit.xyz/mini-apps/introduction
 */

import { ethers } from 'ethers';

// Default configuration
const DEFAULT_CONFIG = {
  appName: 'Dope Chain Wars',
  appIcon: '/assets/icon.png',
  appUrl: typeof window !== 'undefined' ? window.location.origin : 'https://thedopechainwars.com',
};

/**
 * Detects if the current environment is Base App (NOT Farcaster Frame)
 * @returns {boolean} True if running in Base App
 */
export function isBaseAppEnvironment() {
  if (typeof window === 'undefined') return false;
  
  // CRITICAL: Base App and Farcaster BOTH use window.miniKit
  // Differentiate by checking isFrameContext:
  // - Base App: miniKit.isFrameContext === false or undefined
  // - Farcaster Frame: miniKit.isFrameContext === true
  
  if (!window.miniKit) return false;
  
  // If miniKit exists and is NOT a Farcaster frame, it's Base App
  return window.miniKit.isFrameContext !== true;
}

/**
 * Base MiniKit client for Base App integration
 */
class BaseMiniKitClient {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.isInitialized = false;
    this.context = null;
    this.provider = null;
    this.signer = null;
    this.eventHandlers = {
      context: [],
      connect: [],
      disconnect: [],
      error: [],
    };
  }

  /**
   * Check if MiniKit is available dynamically
   * @returns {boolean} True if available
   */
  get isAvailable() {
    return isBaseAppEnvironment();
  }

  /**
   * Initialize the MiniKit client
   * @returns {Promise<boolean>} Success status
   */
  async initialize() {
    if (!this.isAvailable) {
      console.log('[BaseMiniKit] Not in Base App environment');
      return false;
    }

    try {
      const miniKit = window.miniKit;

      // Signal frame is ready
      if (typeof miniKit.setFrameReady === 'function') {
        miniKit.setFrameReady(true);
      }

      // Get initial context (Base App context)
      if (typeof miniKit.getContext === 'function') {
        this.context = await miniKit.getContext();
        console.log('[BaseMiniKit] Context received:', this.context);
      }

      // Set up event listeners
      if (typeof miniKit.on === 'function') {
        miniKit.on('context', this._handleContextUpdate.bind(this));
      }

      // Create ethers provider from window.ethereum (injected by Base App)
      if (window.ethereum) {
        this.provider = new ethers.BrowserProvider(window.ethereum);
      }

      this.isInitialized = true;
      console.log('[BaseMiniKit] ✅ Initialized successfully');
      return true;
    } catch (error) {
      console.error('[BaseMiniKit] Initialization error:', error);
      this._triggerEvent('error', error);
      return false;
    }
  }

  /**
   * Get the current wallet address
   * @returns {Promise<string|null>} Wallet address or null
   */
  async getAddress() {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.provider) {
      return null;
    }

    try {
      // Get signer lazily
      if (!this.signer) {
        this.signer = await this.provider.getSigner();
      }
      return await this.signer.getAddress();
    } catch (error) {
      console.error('[BaseMiniKit] Error getting address:', error);
      return null;
    }
  }

  /**
   * Request wallet connection (triggers Base App wallet prompt)
   * @returns {Promise<string|null>} Connected address or null
   */
  async connect() {
    if (!this.isAvailable) {
      console.log('[BaseMiniKit] Not in Base App environment');
      return null;
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Request accounts via EIP-1193
      if (this.provider) {
        const accounts = await this.provider.send('eth_requestAccounts', []);
        
        if (accounts && accounts.length > 0) {
          const address = accounts[0];
          
          // Get signer
          this.signer = await this.provider.getSigner();
          
          this._triggerEvent('connect', { address });
          console.log('[BaseMiniKit] ✅ Connected:', address);
          return address;
        }
      }

      return null;
    } catch (error) {
      console.error('[BaseMiniKit] Connection error:', error);
      this._triggerEvent('error', error);
      return null;
    }
  }

  /**
   * Sign a message with the connected wallet
   * @param {string} message Message to sign
   * @returns {Promise<string|null>} Signature or null
   */
  async signMessage(message) {
    if (!this.isInitialized || !this.signer) {
      console.error('[BaseMiniKit] Not initialized or no signer');
      return null;
    }

    try {
      return await this.signer.signMessage(message);
    } catch (error) {
      console.error('[BaseMiniKit] Signing error:', error);
      this._triggerEvent('error', error);
      return null;
    }
  }

  /**
   * Open a URL in the Base App browser
   * @param {string} url URL to open
   * @param {Object} options Options
   * @returns {Promise<boolean>} Success status
   */
  async openUrl(url, options = {}) {
    if (!this.isAvailable || !window.miniKit) {
      return false;
    }

    try {
      if (typeof window.miniKit.openUrl === 'function') {
        await window.miniKit.openUrl(url, options);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[BaseMiniKit] Error opening URL:', error);
      return false;
    }
  }

  /**
   * Close the current frame
   * @returns {Promise<boolean>} Success status
   */
  async close() {
    if (!this.isAvailable || !window.miniKit) {
      return false;
    }

    try {
      if (typeof window.miniKit.close === 'function') {
        await window.miniKit.close();
        return true;
      }
      return false;
    } catch (error) {
      console.error('[BaseMiniKit] Error closing frame:', error);
      return false;
    }
  }

  /**
   * Add event listener
   * @param {string} event Event name
   * @param {Function} callback Callback function
   */
  on(event, callback) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].push(callback);
    }
  }

  /**
   * Remove event listener
   * @param {string} event Event name
   * @param {Function} callback Callback function
   */
  off(event, callback) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (handler) => handler !== callback
      );
    }
  }

  /**
   * Handle context update from MiniKit
   * @param {Object} newContext Updated context
   * @private
   */
  _handleContextUpdate(newContext) {
    this.context = { ...this.context, ...newContext };
    this._triggerEvent('context', this.context);
  }

  /**
   * Trigger an event
   * @param {string} event Event name
   * @param {*} data Event data
   * @private
   */
  _triggerEvent(event, data) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[BaseMiniKit] Error in ${event} handler:`, error);
        }
      });
    }
  }
}

// Singleton instance
let instance = null;

/**
 * Get the BaseMiniKitClient instance
 * @param {Object} config Configuration
 * @returns {BaseMiniKitClient} Client instance
 */
export function getBaseMiniKitClient(config = {}) {
  if (!instance) {
    instance = new BaseMiniKitClient(config);
  }
  return instance;
}

export default getBaseMiniKitClient;
